/**
 * Route table.
 *
 * The authenticated area is mounted under `RequireSession` + `WorkspaceBootstrap`,
 * so no business page can render before the session is confirmed and a tenant is
 * active. `/login` stays outside the guard but redirects away once signed in.
 *
 * Pages come from `routePages` (one chunk each, loaded on first visit); layouts
 * render them through `RouteOutlet`, and top-level lazy elements sit in a
 * `RouteBoundary`, so a slow or failed chunk never blanks the shell.
 */

import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { fetchMe } from '../api/platform';
import { isApiError } from '../api/errors';
import { sessionStore, useSession } from './useSession';
import { RequireSession } from './AppShell';
import { RouteBoundary } from './RouteBoundary';
import { intendedPathFromState } from './routerState';
import { WorkspaceBootstrap } from './WorkspaceBootstrap';
import { LoginPage } from '../pages/LoginPage';
import {
  AlertDetailPage,
  AlertsPage,
  BillingPage,
  ContentRevisionPage,
  ContentsPage,
  CrawlersPage,
  DashboardPage,
  DeletionAssessmentPage,
  IntelPage,
  JobsPage,
  MultimodalDemoPage,
  OpinionMonitoringPage,
  PasswordPage,
  PlatformAudit,
  PlatformBilling,
  PlatformEvaluation,
  PlatformEvaluationSet,
  PlatformLayout,
  PlatformOverview,
  PlatformPrivateSources,
  PlatformRuntime,
  PlatformSources,
  PlatformTenantDetail,
  PlatformTenants,
  PlatformUserDetail,
  PlatformUsers,
  PlatformWebsiteRules,
  PlatformWorkspace,
  PropagationAnalysisPage,
  ReportCenterPage,
  ReportDetailPage,
  SupportAccessPage,
  TaskDetailPage,
  TasksPage,
  TopicDetailPage,
  TopicsPage,
} from './routePages';

/** Keeps a signed-in user off the login screen and honours the attempted route. */
function LoginRoute(): JSX.Element {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const intended = intendedPathFromState(location.state);

  useEffect(() => {
    if (session.status !== 'restoring') return;
    if (session.user === null) {
      sessionStore.resolveProbe('unauthorized');
      return;
    }
    const controller = new AbortController();
    void fetchMe(controller.signal).then(
      (user) => {
        sessionStore.updateUser(user);
        if (!controller.signal.aborted && sessionStore.getSnapshot().status === 'restoring') {
          sessionStore.resolveProbe('ok');
        }
      },
      (error: unknown) => {
        if (controller.signal.aborted || sessionStore.getSnapshot().status !== 'restoring') return;
        if (isApiError(error) && error.kind === 'unauthorized') {
          sessionStore.resolveProbe('unauthorized');
        } else {
          sessionStore.resolveProbe('unavailable', isApiError(error) ? error : undefined);
        }
      },
    );
    return () => controller.abort();
  }, [session.status, session.user]);

  useEffect(() => {
    if (session.status === 'authenticated') {
      void navigate(
        session.user?.must_change_password
          ? '/password'
          : (intended ?? (session.user?.is_platform_operator ? '/platform' : '/')),
        { replace: true },
      );
    }
  }, [intended, navigate, session.status, session.user]);

  if (session.status === 'restoring') {
    return (
      <div className="auth">
        <div className="auth__box">
          <div className="auth__title">正在恢复会话…</div>
          <p className="auth__sub">正在确认登录状态，请稍候。</p>
        </div>
      </div>
    );
  }

  return <LoginPage />;
}

/**
 * Redirects a retired page to its new home, keeping any query the old link carried
 * and staying inside the platform workspace prefix when there is one.
 */
function RedirectTo({ path, search }: { readonly path: string; readonly search: string }) {
  const location = useLocation();
  const prefix = location.pathname.match(/^\/platform\/tenants\/[^/]+\/workspace/)?.[0] ?? '';
  const params = new URLSearchParams(location.search);
  new URLSearchParams(search).forEach((value, key) => {
    params.set(key, value);
  });
  return <Navigate to={`${prefix}${path}?${params.toString()}`} replace />;
}

function businessRoutes() {
  return (
    <>
      <Route index element={<DashboardPage />} />
      <Route path="topics" element={<TopicsPage />} />
      <Route path="topics/:id" element={<TopicDetailPage />} />
      <Route path="crawlers" element={<CrawlersPage />} />
      <Route path="crawlers/public" element={<RedirectTo path="/crawlers" search="" />} />
      <Route path="contents" element={<ContentsPage />} />
      <Route path="contents/revisions/:id" element={<ContentRevisionPage />} />
      <Route path="contents/:id" element={<ContentRevisionPage current />} />
      <Route path="contents/:id/deletion-assessment" element={<DeletionAssessmentPage />} />
      <Route
        path="deletion-requests"
        element={<RedirectTo path="/contents" search="tab=deletions" />}
      />
      <Route path="alerts" element={<AlertsPage />} />
      <Route path="alerts/:id" element={<AlertDetailPage />} />
      <Route path="notifications" element={<RedirectTo path="/alerts" search="unread=1" />} />
      <Route path="jobs" element={<JobsPage />} />
      <Route path="reports" element={<ReportCenterPage />} />
      <Route path="reports/:id" element={<ReportDetailPage />} />
      <Route
        path="investigations"
        element={<RedirectTo path="/reports" search="tab=investigations" />}
      />
      <Route path="tasks" element={<TasksPage />} />
      <Route path="tasks/:id" element={<TaskDetailPage />} />
      <Route path="analysis/monitoring" element={<OpinionMonitoringPage />} />
      <Route path="analysis/intel" element={<IntelPage />} />
      <Route path="analysis/multimodal" element={<MultimodalDemoPage />} />
      <Route
        path="analysis/reports"
        element={<RedirectTo path="/reports" search="tab=periodic" />}
      />
      <Route path="analysis/propagation/:contentId" element={<PropagationAnalysisPage />} />
      <Route path="billing" element={<BillingPage />} />
      <Route path="support" element={<SupportAccessPage />} />
      {/* In-shell account page; the standalone /password stays for forced changes. */}
      <Route path="account/password" element={<PasswordPage />} />
    </>
  );
}

export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route
        path="/password"
        element={
          <RequireSession>
            <RouteBoundary>
              <PasswordPage />
            </RouteBoundary>
          </RequireSession>
        }
      />
      <Route
        path="/platform"
        element={
          <RouteBoundary>
            <PlatformLayout />
          </RouteBoundary>
        }
      >
        <Route index element={<PlatformOverview />} />
        <Route path="users" element={<PlatformUsers />} />
        <Route path="users/:userId" element={<PlatformUserDetail />} />
        <Route path="tenants" element={<PlatformTenants />} />
        <Route path="tenants/:tenantId" element={<PlatformTenantDetail />} />
        <Route path="sources" element={<PlatformSources />} />
        <Route path="website-rules" element={<PlatformWebsiteRules />} />
        <Route path="evaluation" element={<PlatformEvaluation />} />
        <Route path="evaluation/:tenantId/:setId" element={<PlatformEvaluationSet />} />
        <Route path="runtime" element={<PlatformRuntime />} />
        <Route path="audit" element={<PlatformAudit />} />
        <Route path="tenants/:tenantId/workspace" element={<PlatformWorkspace />}>
          {businessRoutes()}
          <Route path="private-sources" element={<PlatformPrivateSources />} />
          <Route path="billing/manage" element={<PlatformBilling />} />
        </Route>
      </Route>
      {/*
        The guard wraps a layout route whose children use relative paths. Absolute
        child paths under a pathless parent do not match, which would silently send
        every page to the catch-all redirect.
      */}
      <Route
        path="/"
        element={
          <RequireSession>
            <WorkspaceBootstrap />
          </RequireSession>
        }
      >
        {businessRoutes()}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
